import { dimensionSchema, type QuoteDimension } from "@workspace/api-zod/quantity-dimensions";
import { isConstructionRun, keyMeasurementGroup, measurementReviewSchema, type MeasurementReview } from "@workspace/api-zod/measurement-review";
import type { MeasurementSubject } from "@workspace/api-zod/measurement-review";
import { createSubjectFields, subjectProfile, subjectMeasurementAxis } from "./measurement-subject-fields";
import { keyMeasurementSuggestions } from "./key-measurements";
import { includeDescriptionMeasurements } from "./description-review-measurements";
import { isWallHeightMeasurement } from "./wall-height-measurement";
import { withExtensionReviewFields, initialiseExtensionFootprints } from "./extension-review-measurements";

export interface MeasurementReviewField {
  id: string;
  label: string;
  unit: QuoteDimension["unit"];
  value: string;
  note: string;
  original?: QuoteDimension;
  custom?: boolean;
  subjectId?: string;
  subjectLabel?: string;
  subjectKind?: MeasurementSubject["kind"];
  drawingLimit?: boolean;
  edited?: boolean;
  /** Retained in draft state so a dismissed suggestion cannot reappear on reconciliation. */
  removed?: boolean;
  reviewLabel?: string;
  /** Description-only values aren't independently verified drawing readings. */
  descriptionBasis?: "stated" | "assumed";
  openingEstimate?: boolean;
  wallHeightUnconfirmed?: boolean;
  calculation?: string;
}
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const profiles: { name: string; match: RegExp; fields: [string, QuoteDimension["unit"]][] }[] = [
  { name: "Extension", match: /\b(extension|new build|outbuilding|porch)\b/i,
    fields: [["Extension length", "m"], ["Extension width", "m"]] },
  { name: "Loft", match: /\b(loft|attic)\b/i,
    fields: [["Loft floor length", "m"], ["Loft floor width", "m"]] },
  { name: "Bathroom", match: /\b(bathroom|wet room|en suite|ensuite|shower room)\b/i,
    fields: [["Bathroom length", "m"], ["Bathroom width", "m"]] },
  { name: "Kitchen", match: /\bkitchen\b/i,
    fields: [["Kitchen length", "m"], ["Kitchen width", "m"]] },
  { name: "Roof", match: /\b(roof|roofing|reroof)\b/i,
    fields: [["Roof length", "m"], ["Roof width", "m"]] },
  { name: "Openings", match: /\b(opening|openings|doorway|lintel)\b/i,
    fields: [["Opening width", "m"], ["Opening height", "m"]] },
  { name: "Walls", match: /\b(brickwork|brick wall|masonry|blockwork|infill|partition|wall construction)\b/i,
    fields: [["Wall length", "m"], ["Wall height", "m"]] },
  { name: "External works", match: /\b(patio|paving|driveway|landscaping|decking)\b/i,
    fields: [["Work area length", "m"], ["Work area width", "m"]] },
  { name: "Groundworks", match: /\b(foundations?|footings?|trenches|trench|excavation|underpinning)\b/i,
    fields: [["Trench length", "m"], ["Trench width", "m"], ["Excavation depth", "m"]] },
  { name: "Doors and windows", match: /\b(windows?|doors?)\b/i,
    fields: [["Opening width", "m"], ["Opening height", "m"]] },
  { name: "Room finishes", match: /\b(plaster|plastering|painting|decorating|flooring|boarding|tiling)\b/i,
    fields: [["Room length", "m"], ["Room width", "m"], ["Room height", "m"]] },
];

function mainProfile(context: string[]) {
  const scope = context.join("\n").replace(/\b(?:no|excluding|without)\b[^,.;\n]*/gi, "")
    .replace(/\bexisting\s+(?:rear\s+|side\s+|front\s+)?extension\b/gi, "");
  // Supporting trades should not turn a knock-through into a whole-room survey.
  const priority = ["Extension", "Loft", "Openings", "Bathroom", "Kitchen", "Roof",
    "External works", "Walls", "Groundworks", "Doors and windows", "Room finishes"];
  return priority.map(name => profiles.find(p => p.name === name)!)
    .find(p => p.match.test(scope));
}

function withUnconfirmedWallHeight(fields: MeasurementReviewField[], evidence: unknown[], profile?: string) {
  if (profile !== "Extension" || fields.some(isWallHeightMeasurement)) return fields;
  const rejectedHeight = evidence.some(raw => {
    const parsed = dimensionSchema.safeParse(raw);
    if (!parsed.success) return false;
    const d = parsed.data;
    return d.unit === "m" && /\b(?:extension|wall)\b.*\bheight\b/i.test(d.label) &&
      !isWallHeightMeasurement({ ...d, value: String(d.value), original: d });
  });
  return rejectedHeight ? [...fields, {
    id: "review-wall-height", label: "Wall height", unit: "m" as const, value: "",
    note: "No extension wall height identified. Window and door heights are not wall heights.",
    wallHeightUnconfirmed: true,
  }] : fields;
}

/** This filters the builder's review only. Original technical evidence stays with the drawing analysis. */
function isMainJobMeasurement(field: MeasurementReviewField, profileName?: string) {
  if (field.custom) return true;
  const checklistGroup = keyMeasurementGroup(field.original?.label || field.label, field.unit, field.subjectKind);
  if (checklistGroup) {
    // An opening-only job does not become a whole-room/floor survey because
    // a surrounding room size happened to appear on the drawing.
    if (profileName === "Openings" && ["rooms", "floor", "roof"].includes(checklistGroup) &&
      !["room", "floor", "roof"].includes(field.subjectKind || "")) return false;
    return true;
  }
  if (!["m", "m²", "m³", "each"].includes(field.unit)) return false;
  const label = norm(field.subjectLabel && field.label.startsWith(field.subjectLabel)
    ? field.label.slice(field.subjectLabel.length) : field.label);
  const subjectNoun = field.subjectKind === "groundworks" ? "trench" :
    field.subjectKind === "external" ? "work area" : field.subjectKind || "";
  const scopedLabel = `${subjectNoun} ${label}`;
  if (/\b(padstones?|bearings?|supports?|seating|retained|maximum|minimum|allowable|clearance|offset|embedment|anchorage|bolts?|beams?|columns?|joists?|rafters?|purlins?|flange|reinforcement|rebar|steel|plates?|nails?|screws?)\b/.test(label)) return false;
  if (/\b(?:brick|block|tile|slate|board|bag|roll)\s+(?:size|length|width|height)\b/.test(label)) return false;
  if (!/\b(length|width|height|area|depth|projection|frontage|breadth|span|perimeter|run|count|number)\b/.test(label)) return false;
  if (profileName === "Extension" && /\bheight\b/.test(label)) return isWallHeightMeasurement(field);
  const scopes: Record<string, RegExp> = {
    Extension: /\b(extension|footprint)\b/,
    Loft: /\b(loft|attic|floor|head)\b/,
    Openings: /\b(openings?|doorways?|doors?|windows?)\b/,
    Chimney: /\bchimney\b/,
    Bathroom: /\b(bathroom|wet room|en suite|ensuite|shower room|room)\b/,
    Kitchen: /\b(kitchen|room)\b/,
    Roof: /\b(roof|eaves|ridge)\b/,
    Walls: /\b(walls?|brickwork|blockwork|masonry|infill|partition)\b/,
    "External works": /\b(patio|paving|driveway|deck|decking|work area)\b/,
    Groundworks: /\b(trench|excavation|foundation|footing)\b/,
    "Doors and windows": /\b(openings?|doorways?|doors?|windows?)\b/,
    "Room finishes": /\b(room|floor|ceiling|walls?|bedroom|kitchen|bathroom|living|dining|utility)\b/,
  };
  return (profileName && scopes[profileName] ? scopes[profileName] :
    /\b(openings?|doorways?|doors?|windows?|extension|loft|attic|bathroom|kitchen|bedroom|living|dining|utility|cloakroom|room|floor|ceiling|wall|infill|partition|roof|patio|paving|driveway|deck|decking|trench|excavation|foundation|footing|render|fence|fencing|work)\b/).test(scopedLabel);
}

/** Also tidies older saved review states without confirming hidden technical dimensions. */
export function simplifyMeasurementReviewFields(fields: MeasurementReviewField[], context: string[]) {
  const profile = mainProfile(context);
  const suggestions = profile?.fields ?? [["Work length", "m"], ["Work width", "m"]];
  return keyMeasurementSuggestions(includeDescriptionMeasurements(withUnconfirmedWallHeight(fields.filter(f => f.wallHeightUnconfirmed || f.descriptionBasis || f.removed || f.custom || f.edited || f.original?.source === "entered" ||
    isMainJobMeasurement(f, f.subjectKind ? subjectProfile[f.subjectKind] : profile?.name) &&
    (f.original || f.subjectId || suggestions.some(([label, unit]) => f.unit === unit && norm(f.label) === norm(label)))),
    fields.flatMap(f => f.original ? [f.original] : []), profile?.name),
    context[0] || ""), profile?.name || "");
}

/** Profiles suggest empty fields only; they are not geometry or pricing rules. */
export function createMeasurementReview(evidence: unknown[], context: string[], subjects: unknown[] = []) {
  const subjectFields = createSubjectFields(evidence, context, subjects, isMainJobMeasurement);
  if (subjectFields) {
    const extras = evidence.flatMap(raw => {
      const parsed = dimensionSchema.safeParse(raw);
      if (!parsed.success || subjectFields.fields.some(f => f.original?.id === parsed.data.id)) return [];
      const d = parsed.data;
      if (!(keyMeasurementGroup(d.label, d.unit) &&
        isMainJobMeasurement({ ...d, value: String(d.value), original: d }, mainProfile(context)?.name) ||
        isWallHeightMeasurement({ ...d, value: String(d.value), original: d }))) return [];
      return [{ id: d.id, label: d.label, value: String(d.value), unit: d.unit, note: d.note, original: d }];
    });
    return { ...subjectFields, fields: keyMeasurementSuggestions(includeDescriptionMeasurements(withUnconfirmedWallHeight(
      [...subjectFields.fields, ...extras], evidence, mainProfile(context)?.name), context[0] || ""),
      mainProfile(context)?.name || subjectFields.jobLabel) };
  }
  const profile = mainProfile(context);
  const fields: MeasurementReviewField[] = evidence.flatMap(raw => {
    const parsed = dimensionSchema.safeParse(raw);
    if (!parsed.success) return [];
    const field = { id: parsed.data.id, label: parsed.data.label, unit: parsed.data.unit,
      value: String(parsed.data.value), note: parsed.data.note, original: parsed.data };
    return isMainJobMeasurement(field, profile?.name) ? [field] : [];
  });
  const suggestions = profile?.fields ?? (fields.length ? [] :
    [["Work length", "m"], ["Work width", "m"]] as [string, QuoteDimension["unit"]][]);
  for (const [label, unit] of suggestions) {
    const key = norm(label);
    if (fields.some(f => {
      if (profile?.name === "Extension" && f.unit === unit &&
        subjectMeasurementAxis("extension", f.label) === subjectMeasurementAxis("extension", label)) return true;
      const existing = norm(f.label).replace(/\b(?:doorways?|openings|windows?|doors?)\b/g, "opening")
        .replace(/\b\d+\b/g, "").replace(/\s+/g, " ").trim();
      return f.unit === unit && (existing === key || existing.endsWith(` ${key}`));
    })) continue;
    if (fields.length >= 150) break;
    fields.push({ id: `review-${key.replace(/ /g, "-")}`, label, unit, value: "", note: "" });
  }
  return { fields: keyMeasurementSuggestions(includeDescriptionMeasurements(withUnconfirmedWallHeight(fields, evidence, profile?.name), context[0] || ""), profile?.name || ""), jobLabel: profile?.name || "Your project" };
}

/** Upgrade an already-open/older single-opening review without discarding builder edits. */
function resolveBaseMeasurementReviewFields(fields: MeasurementReviewField[], evidence: unknown[], context: string[]) {
  const axis = (f: MeasurementReviewField) => subjectMeasurementAxis(f.subjectKind, f.label);
  const subjects = [...new Set(fields.map(f => f.subjectId).filter(Boolean))].map(id => {
    const peers = fields.filter(f => f.subjectId === id);
    return { id, label: peers[0].subjectLabel, kind: peers[0].subjectKind,
      dimensionEvidenceIds: [...new Set(peers.flatMap(f => f.original ? [f.original.id] : []))] };
  });
  const expanded = createSubjectFields(evidence, context, subjects, isMainJobMeasurement);
  if (!expanded) return simplifyMeasurementReviewFields(fields, context);
  const ids = new Set(expanded.fields.map(f => f.id));
  return keyMeasurementSuggestions(includeDescriptionMeasurements([...expanded.fields.map(f => {
    const previous = fields.find(old => old.id === f.id) ||
      (f.original ? fields.find(old => old.original?.id === f.original?.id) : undefined) || fields.find(old =>
      old.subjectId === f.subjectId && !isConstructionRun(f.label) && !isConstructionRun(old.label) &&
      axis(f) && axis(old) === axis(f));
    const preserve = previous && (previous.removed || previous.edited || previous.original || previous.value.trim());
    return preserve ? { ...previous, ...f, label: previous.label, value: previous.value,
      edited: previous.edited, removed: previous.removed } : f;
  }), ...fields.filter(f => !ids.has(f.id) && (f.wallHeightUnconfirmed || f.descriptionBasis || f.original || f.removed || f.edited || f.custom ||
    (!f.original && f.value.trim() && isMainJobMeasurement(f))))
    .map(f => f.wallHeightUnconfirmed || f.descriptionBasis || f.original || f.removed || f.edited || f.custom ? f : ({ ...f, custom: true }))], context[0] || ""),
    mainProfile(context)?.name || expanded.jobLabel);
}

export function resolveMeasurementReviewFields(fields: MeasurementReviewField[], evidence: unknown[], context: string[]) {
  return initialiseExtensionFootprints(withExtensionReviewFields(
    resolveBaseMeasurementReviewFields(fields, evidence, context),
    mainProfile(context)?.name === "Extension"));
}

export function buildMeasurementReview(fields: MeasurementReviewField[]): MeasurementReview {
  const measurements = fields.filter(f => !f.removed && f.value.trim() !== "").map(f => {
    const value = Number(f.value);
    if (!Number.isFinite(value) || value < 0) throw new Error(`Enter a valid, non-negative value for ${f.label || "this measurement"}.`);
    if (!f.label.trim()) throw new Error("Give each filled measurement a name identifying its room or work item.");
    const original = f.original;
    return {
      id: f.id, label: f.label.trim(), value, unit: f.unit, source: "entered" as const,
      entryOrigin: original ? original.entryOrigin : "manual" as const,
      ...(original ? {
        evidenceId: original.id,
        originalValue: original.originalValue ?? original.value,
        originalSource: original.originalSource ?? original.source,
      } : {}),
      note: `${original ? value === original.value ? "Confirmed by builder." : "Adjusted by builder." : "Supplied by builder."} ${f.calculation || f.note}`.trim().slice(0, 1000),
    };
  });
  return measurementReviewSchema.parse({
    measurements,
    omittedEvidenceIds: fields.filter(f => !f.removed && f.original && !f.drawingLimit && !f.value.trim()).map(f => f.original!.id),
  });
}
